import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { Alert, Button, TextField } from '@shared';
import { useCountryOptions } from '@/features/organization/useCountryOptions';
import { useTranslations } from '@/i18n/client';
import { EOrganizationType } from '@/services/api/types';

import { useRegistration } from './useRegistration';

/** ISO 3166-1 alpha-2 code for Germany — used to find the country ID in the list. */
const GERMANY_ISO2 = 'DE';

interface OrganizationFormData {
  displayName: string;
  street: string;
  zip: string;
  city: string;
  phone: string;
  email: string;
  vat: string;
  merzCustomerNumber: string;
  acceptPrivacyPolicy: boolean;
  acceptTerms: boolean;
}

/**
 * MDenture registration step 2 — mandatory company info form.
 *
 * Differences from SpectraX's OrganizationStep:
 * - Organization type is always Lab for MDenture/Merz registrations.
 * - Address is always shown and required (no addAddress toggle).
 * - Country is fixed to Germany (DE) and cannot be changed.
 * - Extra field: Merz customer number (optional).
 * - Privacy Policy + Terms & Conditions acceptance checkboxes (required).
 * - Address is automatically both billing and shipping address.
 */
export function OrganizationStep() {
  const { submitOrganization, busy, error, clearError } = useRegistration();
  const t = useTranslations('registration');
  const tStatus = useTranslations('common.status');
  const navigate = useNavigate();
  const { options: countryOptions } = useCountryOptions();

  const germanyOption = countryOptions.find((c) => c.iso2 === GERMANY_ISO2);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OrganizationFormData>({
    defaultValues: {
      displayName: '',
      street: '',
      zip: '',
      city: '',
      phone: '',
      email: '',
      vat: '',
      merzCustomerNumber: '',
      acceptPrivacyPolicy: false,
      acceptTerms: false,
    },
  });

  async function onSubmit(data: OrganizationFormData): Promise<void> {
    clearError();

    const countryID = germanyOption?.id ?? '';

    const succeeded = await submitOrganization({
      displayName: data.displayName.trim(),
      organizationType: EOrganizationType.Lab,
      createSpectraXUnassignedCase: false,
      ...(data.merzCustomerNumber.trim() ? { merzCustomerNumber: data.merzCustomerNumber.trim() } : {}),
      address: {
        name: data.displayName.trim(),
        street: data.street.trim(),
        zip: data.zip.trim(),
        city: data.city.trim(),
        countryID,
        ...(data.phone.trim() ? { phone: data.phone.trim() } : {}),
        email: data.email.trim(),
        vat: data.vat.trim(),
        isBillingAddress: true,
        isShippingAddress: true,
      },
    });

    if (succeeded) {
      navigate('/', { replace: true });
    }
  }

  return (
    <form
      className="md-form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      aria-describedby={error ? 'org-error' : undefined}
    >
      {error ? (
        <div id="org-error">
          <Alert variant="error">{error}</Alert>
        </div>
      ) : null}

      <fieldset disabled={busy}>
        <TextField
          label={t('organization.displayNameLabel')}
          type="text"
          autoFocus
          invalid={Boolean(errors.displayName)}
          {...(errors.displayName?.message ? { message: errors.displayName.message } : {})}
          {...register('displayName', { required: t('organization.errors.displayNameRequired') })}
        />

        <TextField
          label={t('addresses.streetLabel')}
          type="text"
          invalid={Boolean(errors.street)}
          {...(errors.street?.message ? { message: errors.street.message } : {})}
          {...register('street', { required: t('addresses.errors.streetRequired') })}
        />

        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="sm:flex-1">
            <TextField
              label={t('addresses.zipLabel')}
              type="text"
              invalid={Boolean(errors.zip)}
              {...(errors.zip?.message ? { message: errors.zip.message } : {})}
              {...register('zip', { required: t('addresses.errors.zipRequired') })}
            />
          </div>
          <div className="sm:flex-1">
            <TextField
              label={t('addresses.cityLabel')}
              type="text"
              invalid={Boolean(errors.city)}
              {...(errors.city?.message ? { message: errors.city.message } : {})}
              {...register('city', { required: t('addresses.errors.cityRequired') })}
            />
          </div>
        </div>

        {/* Country is fixed to Germany — non-interactive select for visual consistency */}
        <div className="md-field">
          <label className="md-field__label">{t('addresses.countryLabel')}</label>
          <select className="md-field__input" disabled value="DE">
            <option value="DE">Deutschland</option>
          </select>
        </div>

        <TextField label={t('addresses.phoneLabel')} type="text" {...register('phone')} />

        <TextField
          label={t('addresses.emailLabel')}
          type="email"
          invalid={Boolean(errors.email)}
          {...(errors.email?.message ? { message: errors.email.message } : {})}
          {...register('email', {
            required: t('addresses.errors.emailRequired'),
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t('addresses.errors.emailInvalid') },
          })}
        />

        <TextField
          label={t('addresses.vatLabel')}
          type="text"
          invalid={Boolean(errors.vat)}
          {...(errors.vat?.message ? { message: errors.vat.message } : {})}
          {...register('vat', { required: t('addresses.errors.vatRequired') })}
        />

        <TextField
          label={t('organization.merzCustomerNumberLabel')}
          type="text"
          {...register('merzCustomerNumber')}
        />

        <div className="md-field">
          <label className="flex items-center gap-2 max-sm:items-start">
            <input
              type="checkbox"
              {...register('acceptPrivacyPolicy', { required: t('terms.errors.privacyPolicyRequired') })}
              aria-invalid={Boolean(errors.acceptPrivacyPolicy) || undefined}
              aria-describedby={errors.acceptPrivacyPolicy ? 'privacy-error' : undefined}
            />
            <span className="min-w-0 max-sm:wrap-anywhere">
              {t('terms.privacyPolicyPrefix')}{' '}
              <a
                href="https://www.merz-dental.de/datenschutz"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:no-underline"
              >
                {t('terms.privacyPolicyLinkText')}
              </a>
            </span>
          </label>
          {errors.acceptPrivacyPolicy?.message ? (
            <span id="privacy-error" className="md-field__error" role="alert">
              {errors.acceptPrivacyPolicy.message}
            </span>
          ) : null}
        </div>

        <div className="md-field">
          <label className="flex items-center gap-2 max-sm:items-start">
            <input
              type="checkbox"
              {...register('acceptTerms', { required: t('terms.errors.termsRequired') })}
              aria-invalid={Boolean(errors.acceptTerms) || undefined}
              aria-describedby={errors.acceptTerms ? 'terms-error' : undefined}
            />
            <span className="min-w-0 max-sm:wrap-anywhere">
              {t('terms.termsPrefix')}{' '}
              <a
                href="https://www.merz-dental.de/agb"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:no-underline"
              >
                {t('terms.termsLinkText')}
              </a>
            </span>
          </label>
          {errors.acceptTerms?.message ? (
            <span id="terms-error" className="md-field__error" role="alert">
              {errors.acceptTerms.message}
            </span>
          ) : null}
        </div>

        <Button type="submit" className="max-sm:w-full" loading={busy} loadingLabel={tStatus('working')}>
          {t('organization.submitButton')}
        </Button>
      </fieldset>
    </form>
  );
}
